import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { useEffect, useState } from "react";

export function AudioAutoplayPopup() {
  const dispatch = useAppDispatch();
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);
  const [position, setPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  const selectedElement = slide?.elements.find((el) => el.id === selectedId);
  const isAudioElement = selectedElement?.type === "audioPlayer";

  // Track the selected audio element's position, the same way ElementActionButtons does,
  // so this stays visible regardless of the element's own z-index/layer order.
  useEffect(() => {
    if (!isAudioElement) {
      setPosition(undefined);
      return;
    }

    const domElement = document.querySelector(`[data-element-id="${selectedId}"]`);

    const updatePosition = () => {
      if (!domElement) return;
      const rect = domElement.getBoundingClientRect();
      setPosition({ top: rect.top, left: rect.right + 10 });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);

    let observer: MutationObserver | null = null;
    if (domElement) {
      observer = new MutationObserver(updatePosition);
      observer.observe(domElement, { attributes: true, attributeFilter: ["style"] });
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      observer?.disconnect();
    };
  }, [selectedId, isAudioElement]);

  if (!isAudioElement || !selectedElement || selectedElement.type !== "audioPlayer" || !position) {
    return null;
  }

  const handleToggleAutoPlay = () => {
    dispatch(
      updateElementProps({ id: selectedElement.id, props: { autoPlay: !selectedElement.autoPlay } })
    );
  };

  return (
    <div
      data-screenshot-hide="true"
      className="fixed bg-white rounded-lg shadow-xl border border-white/60 z-[90] px-2.5 py-1.5"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none whitespace-nowrap">
        <input type="checkbox" checked={selectedElement.autoPlay} onChange={handleToggleAutoPlay} />
        Autoplay
      </label>
    </div>
  );
}
